<template>
	<div :style="style">
		<GridItem v-for="v in list"
			:key="v.index"
			:index="v.index"
			:sort="v.sort"
			:draggable="draggable"
			:drag-delay="dragDelay"
			:row-count="rowCount"
			:cell-size="cellSize"
			:row-shift="rowShift"
			@dragstart="onDragStart"
			@dragend="onDragEnd"
			@drag="onDrag"
			@click="click"
		>
			<slot name="cell"
				:item="v.item"
				:index="v.index"
				:sort="v.sort"
			/>
		</GridItem>
	</div>
</template>

<script>
import GridItem from './GridItem.vue'

export default {
  name: 'Grid',
  components: { GridItem },
  props: {
    items: { type: Array, default: () => [] },
    cellSize: { type: Number, default: 80 },
    draggable: { type: Boolean, default: false },
    dragDelay: { type: Number, default: 0 },
    sortable: { type: Boolean, default: false },
    center: { type: Boolean, default: false },
  },
  data () {
    return {
      list: []
    }
  },
  computed: {
    gridWidth () {
      return this.cellSize * 4
    },
    height () {
      return Math.ceil(this.items.length / this.rowCount) *
        this.cellSize
    },

    style () {
      return 'height:' + this.height + 'px; width: ' + this.gridWidth + 'px; margin: auto;'
    },

    rowCount () {
      return Math.floor(this.gridWidth / this.cellSize)
    },

    rowShift () {
      if (this.center) {
        let contentWidth = this.items.length * this.cellSize
        let rowShift = contentWidth < this.gridWidth
          ? (this.gridWidth - contentWidth) / 2
          : (this.gridWidth % this.cellSize) / 2

        return Math.floor(rowShift)
      }

      return 0
    }
  },
  watch: {
    items: {
      handler: function (nextItems = []) {
        this.list = nextItems.map((item, index) => {
          return {
            item,
            index: index,
            sort: index
          }
        })
      },
      immediate: true
    }
  },

  methods: {
    /* Returns merged event object */
    wrapEvent (other = {}) {
      return {
        datetime: Date.now(),
        items: this.getListClone(),
        ...other
      }
    },
    /* Returns sorted clone of "list" array */
    getListClone () {
      return this.list
        .slice(0)
        .sort((a, b) => {
          return a.sort - b.sort
        })
    },

    removeItem ({ index }) {
      let removeItem = this.list.find(v => v.index === index)
      let removeItemSort = removeItem.sort

      this.list = this.list
        .filter(v => {
          return v.index !== index
        })
        .map(v => {
          let sort = v.sort > removeItemSort
            ? (v.sort - 1)
            : v.sort

          return { ...v, sort }
        })

      this.$emit('remove', this.wrapEvent({ index }))
    },

    onDragStart (event) {
      this.$emit('dragstart', this.wrapEvent(event))
    },

    onDragEnd (event) {
      this.$emit('dragend', this.wrapEvent(event))
    },

    click (event) {
      this.$emit('click', this.wrapEvent(event))
    },

    onDrag (event) {
      if (this.sortable) {
        this.sortList(event.index, event.gridPosition)
      }

      this.$emit('drag', this.wrapEvent({ event }))
    },

    sortList (itemIndex, gridPosition) {
      let targetItem = this.list.find(item => item.index === itemIndex)
      let targetItemSort = targetItem.sort

      /*
        Normalizing new grid position
      */
      gridPosition = Math.max(gridPosition, 0)
      /*
        If you remove this line you can drag items to positions that
        are further than items array length
      */
      gridPosition = Math.min(gridPosition, this.list.length - 1)

      if (targetItemSort !== gridPosition) {
        this.list = this.list.map(item => {
          if (item.index === targetItem.index) {
            return {
              ...item,
              sort: gridPosition
            }
          }

          const { sort } = item

          if (targetItemSort > gridPosition) {
            if (sort <= targetItemSort && sort >= gridPosition) {
              return {
                ...item,
                sort: sort + 1
              }
            }
          }

          if (targetItemSort < gridPosition) {
            if (sort >= targetItemSort && sort <= gridPosition) {
              return {
                ...item,
                sort: sort - 1
              }
            }
          }

          return item
        })

        this.$emit('sort', this.wrapEvent())
      }
    }
  }
}
</script>

<style scoped>
/* on smaller sizes we add some padding to make the cells larger and give the images some space around */
@media (max-width: 960px) {
	.v-grid-item-wrapper { padding: 10px; }
}
</style>
